import { createInertiaApp, router, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'

void createInertiaApp({
  title: title => title ? `${title} - Divorce Navigator` : 'Divorce Navigator',
  progress: {
    color: 'oklch(0.52 0.1 165)',
    delay: 250,
    showSpinner: false,
    includeCSS: true,
  },

  resolve: (name) => {
    const pages = import.meta.glob<{default: ResolvedComponent}>('../pages/**/*.tsx')
    const importPage = pages[`../pages/${name}.tsx`]
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      return undefined as unknown as { default: ResolvedComponent }
    }
    return importPage()
  },

  setup({ el, App, props }) {
    const tree = (
      <StrictMode>
        <TooltipProvider>
          <App {...props} />
          <Toaster position="top-right" />
        </TooltipProvider>
      </StrictMode>
    )

    if (el.hasChildNodes()) {
      hydrateRoot(el, tree)
    } else {
      createRoot(el).render(tree)
    }

    // Reveal body after React mounts to prevent FOUC
    document.body.style.visibility = 'visible'

    // Refresh stale data when restored from bfcache (PWA back/forward)
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) router.reload()
    })

    router.on('navigate', () => {
      requestAnimationFrame(() => {
        const main = document.getElementById('main-content')
        if (main) {
          main.setAttribute('tabindex', '-1')
          main.focus({ preventScroll: true })
        }
      })
    })
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: false,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
